import React from "react";

/**
 * The floating "talk to us" action.
 *
 * Mounted by the app shell rather than the Header: the Header is a
 * backdrop-blurred bar, and `backdrop-filter` makes an element the containing
 * block for its `position: fixed` descendants — inside it this button would pin
 * itself to the header instead of the viewport.
 *
 * Keeps WhatsApp's own green, since that colour is how the service is
 * recognised, but takes the app's pill shape: icon at rest, widening to show
 * its label on hover and focus where there's room for it.
 */
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/message/NMGM4H3DCGB2C1"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with us on WhatsApp"
      title="Chat with us on WhatsApp"
      // z-40 keeps it above the page but under dialogs (z-70), so it never
      // floats over a confirm prompt.
      // At rest the padding and the icon add up to exactly h-14, so it is a
      // circle; the gap and the extra right padding only appear with the label.
      className="group fixed bottom-6 right-5 z-40 inline-flex h-14 items-center gap-0 rounded-full bg-[#25D366] px-3.5 text-white shadow-lg shadow-[#25D366]/30 outline-none ring-offset-2 transition-all hover:bg-[#1FB855] focus-visible:ring-2 focus-visible:ring-[#25D366] sm:hover:gap-3 sm:hover:pr-5 sm:focus-visible:gap-3 sm:focus-visible:pr-5"
      style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-7 w-7 shrink-0"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.627 5.373-12 12-12 3.193.001 6.206 1.24 8.477 3.514 2.271 2.273 3.515 5.287 3.514 8.49-.003 6.627-5.373 12-12 12-1.99-.001-3.951-.5-5.688-1.448l-6.403 1.553zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.668.149-.198.297-.767.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.076 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.413z" />
      </svg>

      {/* The label only exists on wider screens; on a phone the icon carries it,
          and its accessible name comes from aria-label either way. */}
      <span className="hidden max-w-0 overflow-hidden whitespace-nowrap text-sm font-bold opacity-0 transition-all duration-300 group-hover:max-w-[12rem] group-hover:opacity-100 group-focus-visible:max-w-[12rem] group-focus-visible:opacity-100 sm:inline">
        Chat with us
      </span>
    </a>
  );
};

export default WhatsAppButton;
